/*
    This file contains all the JD Ops Center Work Plan messaging handlers
    that are used when a single work plan is sent.
*/
import { createIntl, createIntlCache, IntlShape } from "@ai360/core/intl";
import * as intlSelectors from "~/intl-provider/selectors";
import { messages } from "~/action-panel/components/rec-module/components/i18n-messages";
import { actions as notificationActions } from "~/notifications";
import { put, select } from "redux-saga/effects";

let intl: IntlShape;
const intlCache = createIntlCache();
let locale: string;

interface MessageObject {
    cropName?: string;
    operatorName?: string;
    recName?: string;
    equipmentName?: string;
    equipmentType?: string;
    productName?: string;
    productType?: string;
}

function createMessageForWorkPlanFeedback(id: number, step: number, message: string) {
    return {
        id: id,
        step: step,
        message: message,
    };
}

const initializeIntl = function* () {
    if (!intl) {
        locale = yield select(intlSelectors.getLocale);
        intl = createIntl({ locale, messages }, intlCache);
    }
};

export const handleCreatingControllerFile = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.creatingPrescriptionFile, {
        recName: messageObject.recName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleAddedCropToWorkPlan = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.addedCropToWorkPlan, {
        cropName: messageObject.cropName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleTakingFirstOperator = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.takingFirstOperator, {
        operatorName: messageObject.operatorName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleAddedOperatorToWorkPlan = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.addedOperatorToWorkPlan, {
        operatorName: messageObject.operatorName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleSkippingEquipment = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.skippingEquipment, {
        equipmentName: messageObject.equipmentName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleAddedEquipmentToWorkPlan = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.addedEquipmentToWorkPlan, {
        equipmentType: messageObject.equipmentType,
        equipmentName: messageObject.equipmentName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCreatedAndAddedOperatorToWorkPlan = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.createdAndAddedOperatorToWorkPlan, {
        operatorName: messageObject.operatorName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleUnArchivedBoundaryInJdOpsCenter = function* () {
    yield initializeIntl();
    const message = intl.formatMessage(messages.unArchivedBoundaryInJdOpsCenter);
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleMadeBoundaryActiveInJdOpsCenter = function* () {
    yield initializeIntl();
    const message = intl.formatMessage(messages.madeBoundaryActiveInJdOpsCenter);
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCreatedProductInJdOpsCenter = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.createdProductInJdOpsCenter, {
        productType: messageObject.productType,
        productName: messageObject.productName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleRetrievedProductFromJdOpsCenter = function* (messageObject: MessageObject) {
    yield initializeIntl();
    const message = intl.formatMessage(messages.retrievedProductFromJdOpsCenter, {
        productType: messageObject.productType,
        productName: messageObject.productName,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateWorkPlanInJdOpsCenter = function* () {
    yield initializeIntl();
    const message = intl.formatMessage(messages.couldNotCreateWorkPlanInJdOpsCenter);
    const messageAndInteger = createMessageForWorkPlanFeedback(null, null, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};
